import { useEffect, useRef } from 'react';

interface PolygonBackgroundProps {
  className?: string;
  dotColor?: string;
  lineColor?: string;
}

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export default function PolygonBackground({ 
  className = '',
  dotColor = '17, 28, 65',
  lineColor = '17, 28, 65'
}: PolygonBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const nodesRef = useRef<Node[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Configuration
    const config = {
      numberOfNodes: 40,
      speed: 1.5, // Increased speed significantly
      nodeDotSize: 3,
      nodeDotColor: dotColor,
      nodeLineColor: lineColor,
      nodeLineAlpha: 0.6, // Increased line opacity
      nodeDotAlpha: 0.8,
      lineDistance: 200
    };

    // Set canvas size
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.offsetWidth;
        canvas.height = parent.offsetHeight;
      }
    };

    // Initialize nodes with random positions and velocities
    const initNodes = () => {
      nodesRef.current = [];
      for (let i = 0; i < config.numberOfNodes; i++) {
        const angle = Math.random() * Math.PI * 2;
        nodesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: Math.cos(angle) * config.speed,
          vy: Math.sin(angle) * config.speed
        });
      }
    };

    // Update nodes with constant velocity
    const updateNodes = () => {
      nodesRef.current.forEach(node => {
        // Move node
        node.x += node.vx;
        node.y += node.vy;
        
        // Bounce off edges
        if (node.x <= 0 || node.x >= canvas.width) {
          node.vx *= -1;
          node.x = Math.max(0, Math.min(canvas.width, node.x));
        }
        if (node.y <= 0 || node.y >= canvas.height) {
          node.vy *= -1;
          node.y = Math.max(0, Math.min(canvas.height, node.y));
        }
      });
    };

    // Draw lines between nearby nodes
    const drawLines = () => {
      ctx.lineWidth = 1.5; // Thicker lines
      
      for (let i = 0; i < nodesRef.current.length; i++) {
        for (let j = i + 1; j < nodesRef.current.length; j++) {
          const node1 = nodesRef.current[i];
          const node2 = nodesRef.current[j];
          
          const dx = node1.x - node2.x;
          const dy = node1.y - node2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < config.lineDistance) {
            const opacity = config.nodeLineAlpha * (1 - distance / config.lineDistance);
            ctx.strokeStyle = `rgba(${config.nodeLineColor}, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(node1.x, node1.y);
            ctx.lineTo(node2.x, node2.y);
            ctx.stroke();
          }
        }
      }
    };

    // Draw nodes
    const drawNodes = () => {
      ctx.fillStyle = `rgba(${config.nodeDotColor}, ${config.nodeDotAlpha})`;
      
      nodesRef.current.forEach(node => {
        ctx.beginPath();
        ctx.arc(node.x, node.y, config.nodeDotSize, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      updateNodes();
      drawLines();
      drawNodes();
      
      animationRef.current = requestAnimationFrame(animate);
    };

    // Initialize
    resizeCanvas();
    initNodes();
    animate();

    // Handle resize
    const handleResize = () => {
      resizeCanvas();
      initNodes();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [dotColor, lineColor]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 ${className}`}
      style={{ pointerEvents: 'none', width: '100%', height: '100%' }}
    />
  );
}
